<template>
  <div class="SupplementFacts-Container" :style="containerStyle">
    <div v-if="!store.hideName" class="SupplementFacts-LabelName" :style="textAlignStyle">
      <!-- eslint-disable vue/no-v-html -->
      <span v-html="formatLabelName(store.name)"></span>
      <!-- eslint-enable vue/no-v-html -->
    </div>
    <div class="SupplementFacts">
      <div ref="headingContainerRef" class="SupplementFacts-HeadingContainer">
        <div ref="headingRef" class="SupplementFacts-Heading" :style="headingStyle">
          Supplement Facts
        </div>
      </div>

      <div v-tippy="getTooltip('serving')" class="SupplementFacts-ServingInfo">
        <div
          v-if="store.singleRowLayout"
          class="SupplementFacts-Row SupplementFacts-Row-SingleLine"
        >
          <div class="SupplementFacts-Col-Half" style="padding-left: 0">
            <span>Serving Size</span>
            {{ store.servingSizeText || "(not set)" }}
          </div>
          <div v-if="showServingsPerContainer" class="SupplementFacts-Col-Half">
            <span>Servings Per Container</span>
            {{ displayServingsPerContainer }}
          </div>
        </div>
        <template v-else>
          <div class="SupplementFacts-Row">
            <div class="SupplementFacts-Col-Full" style="padding-left: 0">
              <span>Serving Size</span>
              {{ store.servingSizeText || "(not set)" }}
            </div>
          </div>
          <div v-if="showServingsPerContainer" class="SupplementFacts-Row">
            <div class="SupplementFacts-Col-Full" style="padding-left: 0">
              <span>Servings Per Container</span>
              {{ displayServingsPerContainer }}
            </div>
          </div>
        </template>
      </div>

      <!-- 21 CFR 101.36(e)(6)(i) requires a heavy bar below the serving declaration. -->
      <div class="SupplementFacts-Bar-Thick"></div>

      <template v-if="isMultiColumnDv">
        <div class="SupplementFacts-Row SupplementFacts-HeaderRow">
          <div class="SupplementFacts-Col-Name"></div>
          <div class="SupplementFacts-Col-Amount"></div>
          <div v-if="shouldShowDvColumn" class="SupplementFacts-Col-DV-Spanning">% Daily Value</div>
        </div>
        <div class="SupplementFacts-Row SupplementFacts-HeaderRow SupplementFacts-AgeRow">
          <div class="SupplementFacts-Col-Name">Amount Per {{ store.amountPerText }}</div>
          <div class="SupplementFacts-Col-Amount"></div>
          <template v-if="shouldShowDvColumn">
            <div
              v-for="(label, idx) in shortAgeCategoryLabels"
              :key="'header-' + idx"
              class="SupplementFacts-Col-DV"
              :style="{ width: dvColumnWidths[idx] + 'px' }"
            >
              {{ label }}
            </div>
          </template>
        </div>
      </template>
      <div v-else class="SupplementFacts-Row SupplementFacts-HeaderRow">
        <div class="SupplementFacts-Col-Name">Amount Per {{ store.amountPerText }}</div>
        <div v-if="shouldShowDvColumn" class="SupplementFacts-Col-DV">% DV</div>
      </div>

      <!-- 21 CFR 101.36(e)(7) requires a light bar below the column headings. -->
      <div class="SupplementFacts-Bar-Medium"></div>

      <div
        v-if="filteredDietaryLines.length"
        v-tippy="getTooltip('dietary-info')"
        data-test-id="dietary-section"
      >
        <template v-for="(line, index) in filteredDietaryLines" :key="'dietary-' + line.dv_key">
          <SupplementFactsRow
            :row-class="getDietaryRowClass(line)"
            :indent-class="getDietaryIndentClass(line, store)"
            :bold-name="false"
            :is-multi-column="isMultiColumnDv"
            :age-categories="displayAgeCategories"
            :column-widths="dvColumnWidths"
            :show-dv-column="shouldShowDvColumn"
            :show-divider="true"
            :divider-class="
              CalculatedValues.getDietaryDividerClass({ store, line, index, filteredDietaryLines })
            "
          >
            <template #name>
              <!-- eslint-disable vue/no-v-html -->
              <span
                v-html="
                  formatLabelName(
                    getDietaryDisplayName(
                      line,
                      CalculatedValues.getDietaryAmount(store.calculatedValues, line),
                      store
                    ),
                    line.italicize_parenthetical
                  )
                "
              ></span>
              <span
                v-if="
                  getLineFootnote(line, index, 'dietary') &&
                  !getLineFootnote(line, index, 'dietary').onAmount
                "
                class="custom-footnote-symbol icon-text"
                v-html="getLineFootnote(line, index, 'dietary').symbol"
              ></span>
              <!-- eslint-enable vue/no-v-html -->
            </template>
            <template #amount>
              <template v-if="!isDietaryAddedSugars(line)"
                >{{ CalculatedValues.getDietaryAmount(store.calculatedValues, line)
                }}{{
                  !shouldShowDvColumn &&
                  CalculatedValues.getDietaryDv(store.calculatedValues, line) === "†"
                    ? noDvSymbol
                    : ""
                }}<!-- eslint-disable vue/no-v-html --><span
                  v-if="getLineFootnote(line, index, 'dietary')?.onAmount"
                  class="custom-footnote-symbol icon-text"
                  v-html="getLineFootnote(line, index, 'dietary').symbol"
                ></span
                ><!-- eslint-enable vue/no-v-html --></template
              >
            </template>
            <template #dv="{ category }">{{
              category
                ? CalculatedValues.getDietaryMultiColumnDv({
                    calculatedValues: store.calculatedValues,
                    line,
                    ageCategory: category,
                  })
                : CalculatedValues.getDietaryDv(store.calculatedValues, line)
            }}</template>
          </SupplementFactsRow>
        </template>
      </div>

      <div
        v-if="filteredDvLines.length"
        v-tippy="getTooltip('dv-lines')"
        data-test-id="dv-lines-section"
      >
        <template
          v-for="({ line, originalIndex }, idx) in filteredDvLines"
          :key="'dv-' + originalIndex"
        >
          <SupplementFactsRow
            :row-class="getRowClass(line)"
            :indent-class="getIndentClass(line, subNutrients)"
            :bold-name="false"
            :is-multi-column="isMultiColumnDv"
            :age-categories="displayAgeCategories"
            :column-widths="dvColumnWidths"
            :show-dv-column="shouldShowDvColumn"
            :dv-class="{ 'is-calculating': store.isCalculating }"
            :show-divider="true"
            :divider-class="
              CalculatedValues.getDvLineDividerClass({ store, line, index: idx, filteredDvLines })
            "
          >
            <template #name>
              <!-- eslint-disable vue/no-v-html -->
              <span
                v-html="
                  formatLabelName(
                    getDisplayName(line, CalculatedValues.getAmount(store.calculatedValues, line)),
                    line.italicize_parenthetical
                  )
                "
              ></span>
              <!-- eslint-enable vue/no-v-html -->
              <!-- eslint-disable vue/no-v-html -- XSS mitigated: renderMarkdown escapes HTML -->
              <span v-if="line.source" class="has-text-weight-normal">
                (<span v-html="renderMarkdown(line.source)"></span>)</span
              >
              <!-- eslint-enable vue/no-v-html -->
              <!-- eslint-disable vue/no-v-html -- getFootnoteSymbol returns sanitized HTML -->
              <span
                v-if="
                  getLineFootnote(line, idx, 'dv') && !getLineFootnote(line, idx, 'dv').onAmount
                "
                class="custom-footnote-symbol icon-text"
                v-html="getLineFootnote(line, idx, 'dv').symbol"
              ></span>
              <!-- eslint-enable vue/no-v-html -->
            </template>
            <template #amount>
              <template v-if="!isAddedSugars(line)"
                >{{ CalculatedValues.getAmount(store.calculatedValues, line)
                }}<!-- eslint-disable vue/no-v-html --><span
                  v-if="getLineFootnote(line, idx, 'dv')?.onAmount"
                  class="custom-footnote-symbol icon-text"
                  v-html="getLineFootnote(line, idx, 'dv').symbol"
                ></span
                ><!-- eslint-enable vue/no-v-html --></template
              >
            </template>
            <template #dv="{ category }">
              {{
                category
                  ? (CalculatedValues.getMultiColumnDv({
                      calculatedValues: store.calculatedValues,
                      line,
                      ageCategory: category,
                    }) ?? "-")
                  : (CalculatedValues.getDv(store.calculatedValues, line) ?? "-")
              }}
            </template>
          </SupplementFactsRow>
        </template>
      </div>

      <div v-if="visibleLines.length" v-tippy="getTooltip('lines')" data-test-id="lines-section">
        <template v-for="(line, index) in visibleLines" :key="'line-' + line._uid">
          <SupplementFactsRow
            :bold-name="false"
            :is-multi-column="isMultiColumnDv"
            :age-categories="displayAgeCategories"
            :column-widths="dvColumnWidths"
            :show-dv-column="shouldShowDvColumn"
            :show-divider="index < visibleLines.length - 1 || visibleBlends.length > 0"
          >
            <template #name>
              <!-- eslint-disable vue/no-v-html -->
              <span
                v-html="
                  formatLabelName(line.name, {
                    italicizeParenthetical: line.italicize_parenthetical,
                    disableSubscripts: true,
                  })
                "
              ></span>
              <!-- eslint-enable vue/no-v-html -->
              <!-- eslint-disable vue/no-v-html -- XSS mitigated: renderMarkdown escapes HTML -->
              <span v-if="line.source" class="has-text-weight-normal">
                (<span v-html="renderMarkdown(line.source)"></span>)</span
              >
              <!-- eslint-enable vue/no-v-html -->
              <!-- eslint-disable vue/no-v-html -->
              <span
                v-if="
                  getLineFootnote(line, index, 'generic') &&
                  !getLineFootnote(line, index, 'generic').onAmount
                "
                class="custom-footnote-symbol icon-text"
                v-html="getLineFootnote(line, index, 'generic').symbol"
              ></span>
              <!-- eslint-enable vue/no-v-html -->
            </template>
            <template #extra>
              <!-- eslint-disable vue/no-v-html -- XSS mitigated: renderMarkdown escapes HTML -->
              <div
                v-if="line.extra_text"
                class="SupplementFacts-ExtraText"
                v-html="renderMarkdown(line.extra_text)"
              ></div>
              <!-- eslint-enable vue/no-v-html -->
            </template>
            <template #amount
              >{{ CalculatedValues.getAmount(store.calculatedValues, line)
              }}{{ !shouldShowDvColumn ? noDvSymbol : ""
              }}<!-- eslint-disable vue/no-v-html --><span
                v-if="getLineFootnote(line, index, 'generic')?.onAmount"
                class="custom-footnote-symbol icon-text"
                v-html="getLineFootnote(line, index, 'generic').symbol"
              ></span
              ><!-- eslint-enable vue/no-v-html --></template
            >
            <template #dv>{{ noDvSymbol }}</template>
          </SupplementFactsRow>
        </template>
      </div>

      <div v-if="visibleBlends.length" v-tippy="getTooltip('blends')" data-test-id="blends-section">
        <template v-for="(blend, index) in visibleBlends" :key="'blend-' + blend._uid">
          <!-- 21 CFR 101.36(e)(5), which (c) extends to blends; sample label (e)(11)(v) draws these. -->
          <SupplementFactsRow
            row-class="SupplementFacts-Row-BlendHeader"
            :bold-name="false"
            :is-multi-column="isMultiColumnDv"
            :age-categories="displayAgeCategories"
            :column-widths="dvColumnWidths"
            :show-dv-column="shouldShowDvColumn"
            :show-divider="hasSeparateConstituents(blend)"
            divider-class="SupplementFacts-Bar-Hairline-Indent"
          >
            <template #name>
              <!-- eslint-disable vue/no-v-html -->
              <span
                v-html="
                  formatLabelName(blend.name, {
                    italicizeParenthetical: blend.italicize_parenthetical,
                    disableSubscripts: true,
                  })
                "
              ></span>
              <!-- eslint-enable vue/no-v-html -->
              <!-- eslint-disable vue/no-v-html -- XSS mitigated: renderMarkdown escapes HTML -->
              <span v-if="blend.source" class="has-text-weight-normal">
                (<span v-html="renderMarkdown(blend.source)"></span>)</span
              >
              <!-- eslint-enable vue/no-v-html -->
              <!-- eslint-disable vue/no-v-html -->
              <span
                v-if="
                  getLineFootnote(blend, index, 'blend') &&
                  !getLineFootnote(blend, index, 'blend').onAmount
                "
                class="custom-footnote-symbol icon-text"
                v-html="getLineFootnote(blend, index, 'blend').symbol"
              ></span>
              <!-- eslint-enable vue/no-v-html -->
            </template>
            <template #extra>
              <!-- eslint-disable vue/no-v-html -- XSS mitigated: renderMarkdown escapes HTML -->
              <div
                v-if="blend.extra_text"
                class="SupplementFacts-ExtraText"
                v-html="renderMarkdown(blend.extra_text)"
              ></div>
              <!-- eslint-enable vue/no-v-html -->
            </template>
            <template #amount
              >{{ CalculatedValues.getBlendAmount(blend)
              }}{{ !shouldShowDvColumn && !blend.hide_dagger ? noDvSymbol : ""
              }}<!-- eslint-disable vue/no-v-html --><span
                v-if="getLineFootnote(blend, index, 'blend')?.onAmount"
                class="custom-footnote-symbol icon-text"
                v-html="getLineFootnote(blend, index, 'blend').symbol"
              ></span
              ><!-- eslint-enable vue/no-v-html --></template
            >
            <template #dv>
              <template v-if="!blend.hide_dagger">{{ noDvSymbol }}</template>
            </template>
          </SupplementFactsRow>

          <div
            v-if="
              getVisibleConstituents(blend).length && (blend.display_mode || 'inline') === 'inline'
            "
            class="SupplementFacts-Row SupplementFacts-Row-BlendList"
          >
            <!-- eslint-disable vue/no-v-html -- XSS mitigated: formatConstituents escapes HTML -->
            <div
              class="SupplementFacts-Col-Full SupplementFacts-Indent"
              v-html="getFormattedConstituents(blend)"
            ></div>
            <!-- eslint-enable vue/no-v-html -->
          </div>
          <template
            v-else-if="
              getVisibleConstituents(blend).length &&
              (blend.display_mode || 'inline') === 'separate'
            "
          >
            <SupplementFactsRow
              v-for="(constituent, cIdx) in getVisibleConstituents(blend)"
              :key="'blend-' + blend._uid + '-constituent-' + cIdx"
              row-class="SupplementFacts-Row-BlendConstituent"
              indent-class="SupplementFacts-Indent"
              :is-multi-column="isMultiColumnDv"
              :age-categories="displayAgeCategories"
              :column-widths="dvColumnWidths"
              :show-dv-column="shouldShowDvColumn"
              :show-divider="cIdx < getVisibleConstituents(blend).length - 1"
              divider-class="SupplementFacts-Bar-Hairline-Indent"
            >
              <template #name>
                <div>
                  <!-- eslint-disable vue/no-v-html -->
                  <!-- XSS mitigated: formatLabelName escapes HTML -->
                  <span
                    v-html="
                      formatLabelName(constituent.name, {
                        italicizeParenthetical: constituent.italicize_parenthetical,
                        disableSubscripts: true,
                      })
                    "
                  ></span>
                  <!-- eslint-enable vue/no-v-html -->
                  <!-- eslint-disable vue/no-v-html -->
                  <!-- XSS mitigated: renderMarkdown escapes HTML, getFootnote returns sanitized symbol -->
                  <span v-if="constituent.source" class="has-text-weight-normal">
                    (<span v-html="renderMarkdown(constituent.source)"></span>)</span
                  >
                  <span
                    v-if="getFootnote(constituent._uid) && !getFootnote(constituent._uid).onAmount"
                    class="custom-footnote-symbol icon-text"
                    v-html="getFootnote(constituent._uid).symbol"
                  ></span>
                </div>
              </template>
              <template #extra>
                <div
                  v-if="constituent.extra_text"
                  class="SupplementFacts-ExtraText"
                  v-html="renderMarkdown(constituent.extra_text)"
                ></div>
                <!-- eslint-enable vue/no-v-html -->
              </template>
              <template #amount></template>
              <template #dv>
                <template v-if="!constituent.hide_dagger">{{ noDvSymbol }}</template>
              </template>
            </SupplementFactsRow>
          </template>
          <template
            v-else-if="
              getVisibleConstituents(blend).length &&
              (blend.display_mode || 'inline') === 'separate_with_values'
            "
          >
            <SupplementFactsRow
              v-for="(constituent, cIdx) in getVisibleConstituents(blend)"
              :key="'blend-' + blend._uid + '-constituent-' + cIdx"
              row-class="SupplementFacts-Row-BlendConstituent"
              indent-class="SupplementFacts-Indent"
              :is-multi-column="isMultiColumnDv"
              :age-categories="displayAgeCategories"
              :column-widths="dvColumnWidths"
              :show-dv-column="shouldShowDvColumn"
              :show-divider="cIdx < getVisibleConstituents(blend).length - 1"
              divider-class="SupplementFacts-Bar-Hairline-Indent"
            >
              <template #name>
                <div>
                  <!-- eslint-disable vue/no-v-html -->
                  <!-- XSS mitigated: formatLabelName escapes HTML -->
                  <span
                    v-html="
                      formatLabelName(constituent.name, {
                        italicizeParenthetical: constituent.italicize_parenthetical,
                        disableSubscripts: true,
                      })
                    "
                  ></span>
                  <!-- eslint-enable vue/no-v-html -->
                  <!-- eslint-disable vue/no-v-html -->
                  <!-- XSS mitigated: renderMarkdown escapes HTML, getFootnote returns sanitized symbol -->
                  <span v-if="constituent.source" class="has-text-weight-normal">
                    (<span v-html="renderMarkdown(constituent.source)"></span>)</span
                  >
                  <span
                    v-if="getFootnote(constituent._uid) && !getFootnote(constituent._uid).onAmount"
                    class="custom-footnote-symbol icon-text"
                    v-html="getFootnote(constituent._uid).symbol"
                  ></span>
                </div>
              </template>
              <template #extra>
                <div
                  v-if="constituent.extra_text"
                  class="SupplementFacts-ExtraText"
                  v-html="renderMarkdown(constituent.extra_text)"
                ></div>
                <!-- eslint-enable vue/no-v-html -->
              </template>
              <template #amount
                >{{ formatConstituentAmount(constituent)
                }}{{ !shouldShowDvColumn && !constituent.hide_dagger ? noDvSymbol : ""
                }}<!-- eslint-disable vue/no-v-html --><span
                  v-if="getFootnote(constituent._uid)?.onAmount"
                  class="custom-footnote-symbol icon-text"
                  v-html="getFootnote(constituent._uid).symbol"
                ></span
                ><!-- eslint-enable vue/no-v-html --></template
              >
              <template #dv>
                <template v-if="!constituent.hide_dagger">{{ noDvSymbol }}</template>
              </template>
            </SupplementFactsRow>
          </template>
          <div v-if="index < visibleBlends.length - 1" class="SupplementFacts-Bar-Hairline"></div>
        </template>
      </div>

      <!-- 21 CFR 101.36(e)(6)(ii)-(iii): the closing bar follows the last listed ingredient, if any. -->
      <div v-if="hasVisibleIngredients" class="SupplementFacts-Bar-Thick"></div>

      <div
        class="SupplementFacts-Footnotes"
        :class="{ 'use-smaller-footnotes': !store.useLargerFootnotes }"
      >
        <div v-if="show2000CalorieFootnote" class="SupplementFacts-Footnote">
          {{ calorie2000Symbol }} Percent Daily Values are based on a 2,000 calorie diet.
        </div>
        <div v-if="showChildren1000CalorieFootnote" class="SupplementFacts-Footnote">
          {{ calorie1000Symbol }} Percent Daily Values are based on a 1,000 calorie diet.
        </div>
        <div v-if="showNoDvFootnote" class="SupplementFacts-Footnote">
          {{ noDvSymbol }} {{ noDvFootnoteText }}
        </div>

        <div
          v-for="note in collectedFootnotes.list"
          :key="note.symbol"
          class="SupplementFacts-Footnote custom-footnote-text"
        >
          <!-- eslint-disable vue/no-v-html --><span
            class="custom-footnote-text-symbol"
            v-html="note.symbol"
          ></span
          ><span v-html="renderMarkdown(note.text)"></span
          ><!-- eslint-enable vue/no-v-html -->
        </div>
      </div>

      <div v-if="store.watermark" class="supplement-watermark"></div>
    </div>
  </div>
</template>

<script setup>
  import { computed, ref, onMounted, onUnmounted } from "vue";
  import { directive as vTippy } from "vue-tippy";
  import { useSupplementLabelStore } from "../stores/supplement-label-store";
  import {
    formatConstituents,
    formatConstituentAmount,
    getDisplayName,
    getDietaryDisplayName,
    formatLabelName,
    getFootnoteSymbol,
    renderMarkdown,
  } from "./utils/formatting";
  import {
    getIndentClass,
    getRowClass,
    getDietaryIndentClass,
    getDietaryRowClass,
    isDietaryAddedSugars,
    isAddedSugars,
  } from "./utils/nutrients";
  import SupplementFactsRow from "./supplement-facts-row.vue";
  import { CalculatedValues, NO_DV_SYMBOL } from "./utils/calculated-values";
  import { sortConstituentsDesc } from "./utils/sorting";

  const store = useSupplementLabelStore();

  const props = defineProps({
    isTouch: { type: Boolean, default: false },
  });

  const SECTION_TITLES = {
    "serving": { desktop: "Serving Information", mobile: "Serving" },
    "dietary-info": { desktop: "Dietary Information", mobile: "Dietary" },
    "dv-lines": { desktop: "Ingredients with Daily Values", mobile: "Ingredients with DV" },
    "lines": { desktop: "Ingredients without DVs", mobile: "Ingredients without DV" },
    "blends": { desktop: "Proprietary Blends", mobile: "Blends" },
    "label-settings": { desktop: "Label Sections and Style", mobile: "Label Settings" },
  };

  const AGE_CATEGORY_COLUMN_WIDTHS = {
    infant: 50,
    children_1_3: 50,
    adult: 65,
    pregnant_lactating: 55,
  };

  function getTooltip(sectionKey) {
    if (!sectionKey || !SECTION_TITLES[sectionKey] || store.isPublic) return null;

    const titles = SECTION_TITLES[sectionKey];
    const title = props.isTouch ? titles.mobile : titles.desktop;

    return {
      content: `Edit in <span class="tooltip-link" data-section="${sectionKey}" style="cursor: pointer; font-weight: bold;">${title}</span>`,
      // placement: auto is default behavior of popper/tippy if not specified, prevents
      // off-screen
      trigger: "mouseenter click",
      allowHTML: true,
      interactive: true,
      theme: "recipal",
      onShow(instance) {
        if (window.matchMedia("print").matches) return false;
      },
      onMount(instance) {
        const links = instance.popper.querySelectorAll(".tooltip-link");
        links.forEach((link) => {
          link.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();
            const key = e.target.closest(".tooltip-link").dataset.section;
            window.dispatchEvent(new CustomEvent("supplement:open-section", { detail: { key } }));
            instance.hide();
          });
        });
      },
    };
  }

  const containerStyle = computed(() => ({
    color: store.textColor || "#000000",
    backgroundColor: store.backgroundColor || "#ffffff",
    width: store.effectiveLabelWidth + "px",
  }));

  const textAlignStyle = computed(() => {
    const style = {};
    if (store.textAlign && store.textAlign !== "left") {
      style.textAlign = store.textAlign;
    }
    return style;
  });

  // Dynamic scaling for "Supplement Facts" heading to span full width
  // Uses letter-spacing + font-size for expansion, scaleX only for shrinking
  const headingRef = ref(null);
  const headingContainerRef = ref(null);

  let resizeObserver = null;
  let debounceTimer = null;
  let lastContainerWidth = 0;

  const BASE_FONT_SIZE = 24;
  const MAX_FONT_SIZE = 32; // Cap font size increase
  const MAX_LETTER_SPACING = 12; // Cap letter spacing (px)
  const HEADING_CHARS = 16; // "Supplement Facts" = 16 characters
  const LETTER_GAPS = HEADING_CHARS - 1; // 15 gaps for letter-spacing

  // Measure natural width once (with base styles) using Canvas API
  // This avoids the flicker from resetting DOM styles
  function measureNaturalWidth() {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    ctx.font = `${BASE_FONT_SIZE}px "Arial Black", "Helvetica Black", sans-serif`;
    return ctx.measureText("Supplement Facts").width;
  }

  let cachedNaturalWidth = 0;

  function updateHeadingScale() {
    const el = headingRef.value;
    const container = headingContainerRef.value;
    if (!el || !container) return;

    const availableWidth = container.getBoundingClientRect().width;
    if (availableWidth <= 0) return;

    // Use cached natural width (measured via Canvas, no DOM flicker)
    if (!cachedNaturalWidth) {
      cachedNaturalWidth = measureNaturalWidth();
    }
    const naturalWidth = cachedNaturalWidth;

    if (naturalWidth <= 0) return;

    const ratio = availableWidth / naturalWidth;

    let letterSpacing = 0;
    let fontSize = BASE_FONT_SIZE;
    let scaleX = 1;

    if (ratio <= 1) {
      // Need to shrink - use scaleX (less noticeable when shrinking)
      scaleX = ratio;
    } else {
      // Need to expand - use letter-spacing and font-size (no scaleX distortion)
      const extraSpace = availableWidth - naturalWidth;
      letterSpacing = extraSpace / LETTER_GAPS;

      if (letterSpacing > MAX_LETTER_SPACING) {
        // Letter-spacing would be too extreme, add font-size increase
        letterSpacing = MAX_LETTER_SPACING;
        const widthWithMaxSpacing = naturalWidth + MAX_LETTER_SPACING * LETTER_GAPS;
        const remainingRatio = availableWidth / widthWithMaxSpacing;

        // Increase font-size proportionally (capped)
        fontSize = Math.min(BASE_FONT_SIZE * remainingRatio, MAX_FONT_SIZE);

        // If still not enough, use small scaleX as last resort
        const estimatedWidth =
          (naturalWidth * fontSize) / BASE_FONT_SIZE + MAX_LETTER_SPACING * LETTER_GAPS;
        if (estimatedWidth < availableWidth) {
          scaleX = availableWidth / estimatedWidth;
        }
      }
    }

    // Apply adjustments directly (no reset needed since we use Canvas measurement)
    el.style.letterSpacing = `${letterSpacing}px`;
    el.style.fontSize = `${fontSize}px`;
    el.style.transform = `scaleX(${scaleX})`;
  }

  function debouncedUpdate() {
    // Skip if width hasn't actually changed
    const container = headingContainerRef.value;
    if (!container) return;

    const currentWidth = container.getBoundingClientRect().width;
    if (currentWidth === lastContainerWidth) return;
    lastContainerWidth = currentWidth;

    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(updateHeadingScale, 16); // ~1 frame
  }

  onMounted(() => {
    setTimeout(updateHeadingScale, 50);

    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(debouncedUpdate);
      const panel = headingContainerRef.value?.closest(".SupplementFacts");
      if (panel) {
        resizeObserver.observe(panel);
      }
    }
  });

  onUnmounted(() => {
    if (resizeObserver) {
      resizeObserver.disconnect();
    }
    clearTimeout(debounceTimer);
  });

  const headingStyle = computed(() => ({
    whiteSpace: "nowrap",
    display: "inline-block",
    transformOrigin: "left center",
  }));

  const isMultiColumnDv = computed(() => store.isMultiColumnDv);
  const displayAgeCategories = computed(
    () => store.calculatedValues?.display_age_categories || store.effectiveDisplayAgeCategories
  );
  const ageCategoryLabels = computed(
    () => store.calculatedValues?.age_category_labels || displayAgeCategories.value
  );

  const shortAgeCategoryLabels = computed(() => ageCategoryLabels.value);

  const dvColumnWidths = computed(() =>
    displayAgeCategories.value.map((cat) => AGE_CATEGORY_COLUMN_WIDTHS[cat] || 55)
  );

  const showServingsPerContainer = computed(() => !store.hideServingsPerContainer);

  const displayServingsPerContainer = computed(() => {
    return store.calculatedValues?.servings_per_container_text || store.servingsPerContainer;
  });

  const show2000CalorieFootnote = computed(() => {
    return store.calculatedValues?.show_2000_calorie_footnote || false;
  });

  const calorie2000Symbol = computed(() => {
    return store.calculatedValues?.calorie_footnote_2000_symbol || "*";
  });

  const showNoDvFootnote = computed(() => {
    return CalculatedValues.shouldShowNoDvFootnote(store);
  });

  const showChildren1000CalorieFootnote = computed(() => {
    return store.calculatedValues?.show_children_1000_calorie_footnote || false;
  });

  const calorie1000Symbol = computed(() => {
    return store.calculatedValues?.calorie_footnote_1000_symbol || "*";
  });

  const subNutrients = computed(() => {
    const parents = store.subNutrientParents || {};
    return Object.keys(parents).filter((key) => key !== "added_sugar");
  });

  const filteredDvLines = computed(() => {
    return CalculatedValues.getVisibleDvLines(store);
  });
  const filteredDietaryLines = computed(() => {
    return CalculatedValues.getFilteredDietaryLines(store);
  });

  const visibleBlends = computed(() => {
    return CalculatedValues.getVisibleBlends(store);
  });

  const visibleLines = computed(() => {
    return CalculatedValues.getVisibleNoDvLines(store);
  });

  const hasVisibleIngredients = computed(() => {
    return (
      filteredDietaryLines.value.length > 0 ||
      filteredDvLines.value.length > 0 ||
      visibleLines.value.length > 0 ||
      visibleBlends.value.length > 0
    );
  });

  const getVisibleConstituents = (blend) => {
    if (!blend.constituents) return [];
    return sortConstituentsDesc(blend.constituents.filter((constituent) => constituent.name));
  };

  const hasSeparateConstituents = (blend) => {
    const displayMode = blend.display_mode || "inline";
    return displayMode !== "inline" && getVisibleConstituents(blend).length > 0;
  };

  const shouldShowDvColumn = computed(() => {
    return CalculatedValues.hasAnyEstablishedDv(store);
  });

  const noDvSymbol = NO_DV_SYMBOL;

  const noDvFootnoteText = computed(() => {
    return isMultiColumnDv.value
      ? "Daily Value not established."
      : "Daily Value (DV) not established.";
  });

  // --- Custom Footnotes Logic ---
  const collectedFootnotes = computed(() => {
    const map = new Map(); // key (string) -> { symbol, text, onAmount }
    const list = []; // { symbol, text } for legend
    let counter = 1;
    let hasCustom = false;

    // Helper to process a line
    const processLine = (line, key) => {
      if (line.footnote_text) {
        let symbol = "";
        if (line.custom_footnote_char) {
          symbol = getFootnoteSymbol(line.custom_footnote_char);
          hasCustom = true;
        } else {
          symbol = getFootnoteSymbol(counter++);
        }

        map.set(key, {
          symbol,
          text: line.footnote_text,
          onAmount: line.footnote_on_amount,
        });

        list.push({ symbol, text: line.footnote_text });
      }
    };

    // 1. Dietary Lines (use dv_key)
    filteredDietaryLines.value.forEach((line) => processLine(line, `dietary-${line.dv_key}`));

    // 2. DV Lines (use _uid)
    filteredDvLines.value.forEach(({ line }) => {
      if (line._uid) processLine(line, line._uid);
    });

    // 3. Generic Lines (use _uid)
    visibleLines.value.forEach((line) => {
      if (line._uid) processLine(line, line._uid);
    });

    // 4. Blends and Constituents (use _uid)
    visibleBlends.value.forEach((blend) => {
      if (blend._uid) processLine(blend, blend._uid);

      getVisibleConstituents(blend).forEach((constituent) => {
        if (constituent._uid) processLine(constituent, constituent._uid);
      });
    });

    return { map, list, hasCustom };
  });

  const getFootnote = (key) => collectedFootnotes.value.map.get(key);

  const getLineFootnote = (line, index, type) => {
    let key;
    if (type === "dietary") {
      key = `dietary-${line.dv_key}`;
    } else {
      key = line._uid;
    }

    return getFootnote(key);
  };

  const getFormattedConstituents = (blend) => {
    const decorated = getVisibleConstituents(blend).map((c) => {
      const footnote = getFootnote(c._uid);
      return {
        ...c,
        footnoteHtml: footnote ? footnote.symbol : null,
      };
    });

    return formatConstituents(decorated);
  };
</script>

<style lang="scss" scoped>
  @import "~label/css/labels/supplements/supplement_facts";

  .is-calculating {
    animation: pulse-transparency 0.8s ease-in-out infinite;
  }

  @keyframes pulse-transparency {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.4;
    }
  }

  .SupplementFacts-LabelName {
    font-family: "Lato", sans-serif;
    color: rgb(10, 10, 10);
    font-size: 22px;
    line-height: 30px;
    margin-bottom: 0.75em;
    font-weight: bold;
  }

  .SupplementFacts-AgeLabel {
    font-size: 0.7em;
    font-weight: normal;
    white-space: nowrap;
  }

  .SupplementFacts-Col-DV-Spanning {
    flex: 1 1 auto;
    font-size: 11px;
    font-weight: 700;
    text-align: right;
  }

  .SupplementFacts-AgeRow {
    padding-bottom: 0;

    .SupplementFacts-Col-DV {
      font-size: 9px;
      font-weight: 700;
    }
  }

  .SupplementFacts-Row-AddedSugars {
    font-weight: normal;
  }

  :deep(.custom-footnote-symbol) {
    font-weight: normal !important;
    font-size: 11px !important;
    margin-left: 0.3em;
    position: relative;
    top: 1px;
  }

  .custom-footnote-text {
    font-size: inherit;
  }

  .custom-footnote-text-symbol {
    margin-right: 0.3em;
  }

  // DO NOT DELETE UNTIL CONFIRMATION - keeping conditional custom footnotes
  // logic for potential future use
  // .SupplementFacts-Footnotes.has-custom-footnotes {
  //   .SupplementFacts-Footnote {
  //     font-size: 11px;
  //     line-height: 1.3;
  //   }
  // }

  .SupplementFacts-Footnotes {
    .SupplementFacts-Footnote {
      font-size: 11px;
      line-height: 1.3;
    }

    &.use-smaller-footnotes {
      .SupplementFacts-Footnote {
        font-size: 9px;
        line-height: 1.2;
      }
    }
  }
</style>
